/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { expressionLibraryService } from '../services';
import type { CreateSharedExpressionInput, UpdateSharedExpressionInput } from '../types/expression-library';
import type { ExpressionNameLookup } from '../components/shared/editors/expression-summary';
import type { MacroExpression } from '../types/decorator-expression';
import { entryVersion } from '../components/shared/editors/shared-expression-version';

export const EXPRESSIONS_KEY = 'expressions';

export function useExpressionLibrary() {
  return useQuery({
    queryKey: [EXPRESSIONS_KEY],
    queryFn: () => expressionLibraryService.listExpressions(),
  });
}

export function useCreateExpression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSharedExpressionInput) => expressionLibraryService.createExpression(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPRESSIONS_KEY] }),
  });
}

export function useUpdateExpression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSharedExpressionInput }) =>
      expressionLibraryService.updateExpression(id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPRESSIONS_KEY] }),
  });
}

export function useDeleteExpression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => expressionLibraryService.deleteExpression(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPRESSIONS_KEY] }),
  });
}

export function useToggleExpression() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      expressionLibraryService.toggleExpression(id, enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [EXPRESSIONS_KEY] }),
  });
}

/**
 * **Visibility and ownership are authored in the library editor, not on a row.**
 *
 * `useSetExpressionVisibility` and `useTransferExpressionOwner` were the list row's one-click
 * gestures, and `X44` took ownership off the list (`E5`, `#1273`) — the editor's
 * `ObjectMetadataPanel` writes both through the ordinary save, which is one `PUT` either way.
 * Removed rather than left exported with no caller: pre-pilot, an unused export is a second
 * authoring path waiting to be found by somebody who assumes it is the one in use.
 */

/**
 * **A name lookup for the shared expressions a row may address** (`XS5`, `#1066`).
 *
 * A collapsed row's summary is produced by a pure function, so the name has to be handed in — and a
 * surface holds the library **once**, above its rows, rather than each row asking. That is the
 * difference between one request and one per row, and it is also what keeps `summarizeMacroExpression`
 * a function rather than a component.
 *
 * It returns `undefined` for anything it cannot name, including while the library is loading, which
 * is what makes the fallback honest: the summary then reads *shared expression* rather than a name it
 * does not have.
 */
export function useExpressionNameLookup(): ExpressionNameLookup {
  const { data } = useExpressionLibrary();
  return React.useMemo(() => {
    const byId = new Map(
      (data ?? []).map((entry) => [
        entry.id,
        {
          name: entry.name,
          expression: entry.expression as MacroExpression | undefined,
          version: entryVersion(entry),
        },
      ]),
    );
    return (ref: string) => byId.get(ref);
  }, [data]);
}
